import { useState, useEffect } from 'react';
import { VStack, Text, Heading, View, ScrollView } from 'native-base';

// Components
import { PrimaryButton, TextField } from '../../';

import {
  Auth,
  getMultiFactorResolver,
  MultiFactorError,
  MultiFactorResolver,
  PhoneAuthProvider,
  PhoneMultiFactorGenerator,
  RecaptchaVerifier,
} from 'firebase/auth';

import { analytics } from '@breakroom-nx/apis';
import { logEvent } from 'firebase/analytics';

// Redux
import { useAppDispatch, openModal } from '@breakroom-nx/redux';

import * as Yup from 'yup';

import { Formik, ErrorMessage } from 'formik';

// Assets
import { theme } from '@breakroom-nx/assets';

import { InputType, Status } from '@breakroom-nx/helpers';

const codeRegExp = /^[0-9a-z]+$/;
const CodeScheme = Yup.object().shape({
  code: Yup.string()
    .matches(codeRegExp, 'Only letters and numbers allowed')
    .min(6)
    .max(6)
    .required('Code required'),
});

const initialValues = {
  code: '',
};

export interface Validate2FACodeViewProps {
  auth: Auth;
  error?: MultiFactorError;
}

export function Validate2FACodeView({ auth, error }: Validate2FACodeViewProps) {
  // Redux
  const dispatch = useAppDispatch();
  const [errorFeedback, setErrorFeedback] = useState<string | null>(null);
  const [verID, setVerID] = useState<string | null>(null);

  // User has Multi-Factor Authentication Enabled
  let resolver: MultiFactorResolver | undefined = undefined;
  const selectedIndex = 0;

  const sendCode = () => {
    // Ask user which second factor to use.
    if (resolver) {
      const recaptchaVerifier = new RecaptchaVerifier(
        'phone-auth-button',
        {
          size: 'invisible',
          callback: function (response: any) {
            // reCAPTCHA solved, you can proceed with
            // phoneAuthProvider.verifyPhoneNumber(...).
            // console.log(`Response: ${response}`);
          },
        },
        auth
      );

      if (
        resolver.hints[selectedIndex].factorId ===
        PhoneMultiFactorGenerator.FACTOR_ID
      ) {
        const phoneInfoOptions = {
          multiFactorHint: resolver.hints[selectedIndex],
          session: resolver.session,
        };
        const phoneAuthProvider = new PhoneAuthProvider(auth);
        // Send SMS verification code
        phoneAuthProvider
          .verifyPhoneNumber(phoneInfoOptions, recaptchaVerifier)
          .then(function (verificationId) {
            // console.log(`Code Sent`);
            setVerID(verificationId);
          });
      } else {
        dispatch(
          openModal({
            modalType: 'Alert',
            modalProps: {
              status: Status.error,
              title: 'Unsupported Second Factor',
            },
          })
        );
      }
    }
  };

  useEffect(() => {
    if (error) {
      resolver = getMultiFactorResolver(auth, error);
    }
  }, []);

  useEffect(() => {
    sendCode();
  }, []);

  return (
    <ScrollView
      flex={1}
      maxHeight={'100%'}
      showsVerticalScrollIndicator={true}
      _contentContainerStyle={{
        pb: 8,
      }}
    >
      <VStack space={7} alignItems={'flex-start'} textAlign={'center'}>
        <VStack space={5}>
          <Heading mt={7} fontSize={30} lineHeight={34}>
            2FA Code
          </Heading>
          <Text
            fontFamily={'heading'}
            fontSize={16}
            color={theme.colors.surface[400]}
            fontWeight={600}
            mb={'3px'}
          >
            Please check your phone for the code that was just sent and paste it
            here.
          </Text>
        </VStack>

        <Formik
          initialValues={initialValues}
          validationSchema={CodeScheme}
          onSubmit={(values, { setSubmitting }) => {
            setSubmitting(false);
            if (resolver) {
              const cred = PhoneAuthProvider.credential(verID!, values.code);
              const multiFactorAssertion =
                PhoneMultiFactorGenerator.assertion(cred);
              resolver
                .resolveSignIn(multiFactorAssertion)
                .then((userCredential) => {
                  // console.log(`Correct Code!`);
                  dispatch(
                    openModal({
                      modalType: 'Alert',
                      modalProps: {
                        status: Status.success,
                        title: 'Successfully signed in!',
                      },
                    })
                  );
                  // logEvent(analytics, 'login');
                })
                .catch((error) => {
                  const errorCode = error.code;
                  if (errorCode === 'auth/invalid-verification-code') {
                    setErrorFeedback('Invalid Verification Code');
                  } else {
                    dispatch(
                      openModal({
                        modalType: 'Alert',
                        modalProps: {
                          status: Status.error,
                          title:
                            'Two-factor authentication error. Please try again.',
                        },
                      })
                    );
                  }
                });
            }
          }}
        >
          {({
            values,
            isSubmitting,
            handleChange,
            handleBlur,
            handleSubmit,
          }) => (
            <>
              <VStack space={'3'} width={'full'} textAlign={'center'}>
                <TextField
                  disabled={isSubmitting}
                  type={InputType.Code}
                  value={values.code}
                  onChangeText={handleChange('code')}
                  onBlur={handleBlur('code')}
                  autoCapitalize={'characters'}
                  textAlign={'center'}
                  maxLength={6}
                  // autoFocus
                />
                <Text variant={'error'}>
                  {errorFeedback ? errorFeedback : <ErrorMessage name="code" />}
                </Text>
              </VStack>
              <VStack space={3} width={'full'} mt={7}>
                <PrimaryButton
                  disabled={isSubmitting}
                  isLoading={isSubmitting}
                  text={'Verify'}
                  onPress={handleSubmit}
                />
              </VStack>
              <div id="phone-auth-button" />
            </>
          )}
        </Formik>
      </VStack>
    </ScrollView>
  );
}

export default Validate2FACodeView;
